import { Container, Graphics, Sprite, Texture, Color } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { THEME } from '@config/theme.config';
import { AssetManager } from '@core/Assets';
import { AssetService } from '@core/Assets';

export interface PlaneInitOptions {
  spineKey?: string;
  defaultScale?: number;
  assetSubDir?: string | false;
}

export class Plane extends Container {
  protected plane?: Spine | Sprite | Graphics;
  public baseScale: number = 1.0;

  constructor(
    protected assetManager: AssetManager,
    protected assetService: AssetService
  ) {
    super();
    this.eventMode = 'static';
  }

  async init(slug: string, options: PlaneInitOptions = {}): Promise<void> {
    try {
      const spineKey = options.spineKey ?? 'plane';
      const spineAnim = await this.assetService.loadSpine(slug, spineKey, {
        scale: 1.0,
        assetSubDir: options.assetSubDir,
      });
      if (spineAnim) {
        const defaultScale = options.defaultScale ?? 2.0;
        spineAnim.scale.set(defaultScale);
        this.baseScale = defaultScale;

        const animNames = spineAnim.skeleton.data.animations.map(
          (a: any) => a.name
        );

        // Use the 'animation' animation when available, otherwise fallback to other animations
        // debugger;
        const hasAnimation = (name: string) =>
          !!spineAnim.skeleton.data.findAnimation(name);

        if (hasAnimation('animation')) {
          spineAnim.state.setAnimation(0, 'animation', true);
        } else if (hasAnimation('Fly')) {
          spineAnim.state.setAnimation(0, 'Fly', true);
        } else if (animNames.length > 0) {
          spineAnim.state.setAnimation(0, animNames[0], true);
          console.log('Playing custom animation ', animNames[0]);
        } else {
          console.warn('[Plane] ❌ No animations found!');
        }

        this.plane = spineAnim as any;
        this.addChild(spineAnim as any);
        console.log('[Plane] Spine plane loaded successfully');
        return;
      }
    } catch (error) {
      console.error(
        '[Plane] Failed to load spine plane, using fallback:',
        error
      );
      console.error('[Plane] Error details:', error);
    }

    console.log(`[Plane] Falling back to image plane for ${slug}`);
    await this.createImagePlane(slug);
  }

  protected async createImagePlane(slug: string): Promise<void> {
    try {
      let texture = this.assetManager.getTexture(slug, 'spine/Plane.png');

      if (!texture) {
        const loadedTexture = await this.assetManager.loadAsset(
          slug,
          'spine/Plane.png'
        );
        texture = loadedTexture as Texture;
      }

      const planeSprite = new Sprite(texture);
      planeSprite.anchor.set(0.5);
      planeSprite.scale.set(0.5);
      this.baseScale = 0.5;

      this.plane = planeSprite;
      this.addChild(planeSprite);
      console.log('[Plane] Using image plane');
    } catch (error) {
      console.error('[Plane] Failed to load plane image:', error);

      const fallbackPlane = new Graphics()
        .rect(-15, -10, 30, 20)
        .fill(new Color(THEME.accent).toNumber());
      fallbackPlane.scale.set(2.0);
      this.baseScale = 2.0;

      this.plane = fallbackPlane;
      this.addChild(fallbackPlane);
      console.log('[Plane] Using fallback rectangle plane');
    }
  }

  updatePosition(x: number, y: number, rotation: number): void {
    this.position.set(x, y);
    this.rotation = rotation;
  }

  setScale(s: number): void {
    this.scale.set(s);
  }

  // Base animation methods - can be overridden by game-specific implementations
  private hasSpineAnimation(name: string): boolean {
    return (
      this.plane instanceof Spine &&
      !!this.plane.skeleton.data.findAnimation(name)
    );
  }

  handleCrash(): void {
    if (this.plane instanceof Spine && this.hasSpineAnimation('Crash')) {
      this.plane.state.setAnimation(0, 'Crash', false);
    }
  }

  handleFlying(): void {
    if (this.plane instanceof Spine) {
      if (this.hasSpineAnimation('animation')) {
        this.plane.state.setAnimation(0, 'animation', true);
        console.log('[Plane] Flying with animation: animation (looping)');
      } else if (this.hasSpineAnimation('Fly')) {
        this.plane.state.setAnimation(0, 'Fly', true);
        console.log('[Plane] Flying with animation: Fly (looping)');
      }
    }
  }

  // Get plane configuration - can be overridden by game-specific implementations
  getPlaneConfig() {
    return {
      defaultScale: 2.0,
      fallbackScale: 2.0,
      imageScale: 0.5,
      spinePath: 'spine/Plane.json',
      atlasPath: 'spine/Plane.atlas',
      imagePath: 'spine/Plane.png',
    };
  }
}
